/**
 * Expenditure entry + list — admin can record outflows with optional voucher upload.
 */
import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { api, formatApiErrorDetail } from "@/lib/api";

const ROLE_TO_SCOPE = {
  hostel_admin: "hostel",
  school_admin: "school",
  super_admin: "platform",
};
import { useAuth } from "@/lib/auth-context";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CloudArrowUp, Plus, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

const fmt = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(Number(n) || 0);

const initial = {
  date: new Date().toISOString().slice(0, 10),
  category: "salaries",
  payee: "",
  amount: "",
  mode: "bank",
  description: "",
  voucher_no: "",
  school_id: "",
  hostel_id: "",
  expense_group: "",
  expense_head: "",
  expense_subhead: "",
  paid_to_name: "",
  paid_to_pan: "",
  payment_reference: "",
  gst_number: "",
  gst_amount: "",
  tds_section: "",
  tds_amount: "",
  project_code: "",
  donor_id: "",
  csr_project: "",
};

export default function Expenditure() {
  const { user } = useAuth();
  const [form, setForm] = useState(initial);
  const [taxonomy, setTaxonomy] = useState({ categories: [], modes: [] });
  const [schools, setSchools] = useState([]);
  const [hostels, setHostels] = useState([]);
  const [heads, setHeads] = useState([]);
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [voucherFor, setVoucherFor] = useState(null);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const refresh = () => api.get("/expenditures").then((r) => setRows(r.data || []));
  useEffect(() => {
    api.get("/expenditures/categories").then((r) => setTaxonomy(r.data));
    api.get("/admin/schools").then((r) => setSchools(r.data || [])).catch(() => {});
    api.get("/admin/hostels").then((r) => setHostels(r.data || [])).catch(() => {});
    const scope = ROLE_TO_SCOPE[user?.role] || "";
    const url = scope ? `/admin/expense-heads?scope=${scope}` : "/admin/expense-heads";
    api.get(url).then((r) => setHeads(r.data || [])).catch(() => setHeads([]));
    refresh();
  }, [user]);
  useEffect(() => {
    if (user?.role === "school_admin" && user.school_id) setForm((f) => ({ ...f, school_id: user.school_id }));
    if (user?.role === "hostel_admin" && user.hostel_id) setForm((f) => ({ ...f, hostel_id: user.hostel_id }));
  }, [user]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.payee.trim()) { toast.error("Payee is required"); return; }
    if (!form.amount) { toast.error("Amount is required"); return; }
    if (!form.school_id && !form.hostel_id) { toast.error("Pick a school or hostel"); return; }
    setBusy(true);
    try {
      const payload = { ...form, amount: Number(form.amount) };
      // Backend rejects empty strings on Optional[float] fields — drop them.
      ["gst_amount", "tds_amount"].forEach((k) => {
        if (payload[k] === "") delete payload[k];
        else if (payload[k] != null) payload[k] = Number(payload[k]);
      });
      // Trim other empty advanced strings to null so Mongo doesn't store "".
      ["expense_group", "expense_head", "expense_subhead", "paid_to_name", "paid_to_pan",
        "payment_reference", "gst_number", "tds_section", "project_code", "donor_id", "csr_project"
      ].forEach((k) => { if (payload[k] === "") delete payload[k]; });
      Object.keys(payload).forEach((k) => { if (payload[k] === "" || payload[k] == null) delete payload[k]; });
      const res = await api.post("/expenditures", payload);
      toast.success(`Expenditure recorded: ${fmt(res.data.amount)}`);
      setVoucherFor(res.data);    // open voucher upload UX
      setForm({ ...initial, school_id: form.school_id, hostel_id: form.hostel_id });
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const uploadVoucher = async (expId, file) => {
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/expenditures/${expId}/voucher`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Voucher attached");
      setVoucherFor(null);
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    }
  };

  const total = rows.reduce((a, r) => a + Number(r.amount || 0), 0);

  return (
    <DashboardLayout title="Expenditure" subtitle="Record outflows. Attach a voucher (PDF/JPG/PNG) for audit trail.">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <form onSubmit={submit} className="vs-card p-5 space-y-3 lg:col-span-1" data-testid="exp-form">
          <div className="eyebrow flex items-center gap-1.5"><Plus size={12} /> Record expenditure</div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Date *</Label><Input data-testid="exp-date" required type="date" value={form.date} onChange={(e) => set("date", e.target.value)} className="bg-white" /></div>
            <div><Label>Mode</Label>
              <Select value={form.mode} onValueChange={(v) => set("mode", v)}>
                <SelectTrigger data-testid="exp-mode" className="bg-white"><SelectValue /></SelectTrigger>
                <SelectContent>{taxonomy.modes.map((m) => <SelectItem key={m} value={m}>{m.toUpperCase()}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Category *</Label>
            <Select value={form.category} onValueChange={(v) => set("category", v)}>
              <SelectTrigger data-testid="exp-category" className="bg-white"><SelectValue /></SelectTrigger>
              <SelectContent>{taxonomy.categories.map((c) => <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {/* ----- Expense head taxonomy (group → head) ----- */}
          <div>
            <Label>Expense Head <span className="text-[10px] text-[var(--vs-text-secondary)] font-normal">(detailed)</span></Label>
            <Select value={form.expense_head || "none"} onValueChange={(v) => {
              if (v === "none") { set("expense_head", ""); set("expense_group", ""); return; }
              const [group, head] = v.split("||");
              set("expense_group", group);
              set("expense_head", head);
            }}>
              <SelectTrigger data-testid="exp-head" className="bg-white"><SelectValue placeholder="Pick an expense head" /></SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="none">None</SelectItem>
                {Object.entries(heads.reduce((acc, h) => { (acc[h.group] = acc[h.group] || []).push(h); return acc; }, {})).map(([group, items]) => (
                  <React.Fragment key={group}>
                    <div className="px-2 py-1 text-[10px] uppercase tracking-widest font-mono text-[var(--vs-text-secondary)]">{group}</div>
                    {items.map((h) => <SelectItem key={h.id} value={`${h.group}||${h.head}`} data-testid={`exp-head-opt-${h.id}`}>{h.head}</SelectItem>)}
                  </React.Fragment>
                ))}
              </SelectContent>
            </Select>
            {form.expense_head && (
              <div className="mt-1.5">
                <Label>Sub-head <span className="text-[10px] text-[var(--vs-text-secondary)] font-normal">(optional)</span></Label>
                <Input data-testid="exp-subhead" value={form.expense_subhead} onChange={(e) => set("expense_subhead", e.target.value)} className="bg-white" placeholder="e.g. Q1 - Wing A" />
              </div>
            )}
          </div>
          <div><Label>Payee *</Label><Input data-testid="exp-payee" required value={form.payee} onChange={(e) => set("payee", e.target.value)} className="bg-white" placeholder="e.g. Vendor name" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Amount (INR) *</Label><Input data-testid="exp-amount" required type="number" min="1" step="0.01" value={form.amount} onChange={(e) => set("amount", e.target.value)} className="bg-white font-mono" /></div>
            <div><Label>Voucher # <span className="text-[10px] text-[var(--vs-text-secondary)] font-normal">(optional)</span></Label><Input data-testid="exp-voucher-no" value={form.voucher_no} onChange={(e) => set("voucher_no", e.target.value)} className="bg-white font-mono" /></div>
          </div>
          <div>
            <Label>School <span className="text-[10px] text-[var(--vs-text-secondary)] font-normal">(or hostel below)</span></Label>
            <Select value={form.school_id || "none"} onValueChange={(v) => set("school_id", v === "none" ? "" : v)}>
              <SelectTrigger data-testid="exp-school" className="bg-white"><SelectValue placeholder="None" /></SelectTrigger>
              <SelectContent><SelectItem value="none">None</SelectItem>{schools.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Hostel</Label>
            <Select value={form.hostel_id || "none"} onValueChange={(v) => set("hostel_id", v === "none" ? "" : v)}>
              <SelectTrigger data-testid="exp-hostel" className="bg-white"><SelectValue placeholder="None" /></SelectTrigger>
              <SelectContent><SelectItem value="none">None</SelectItem>{hostels.map((h) => <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Description</Label><Textarea data-testid="exp-description" value={form.description} onChange={(e) => set("description", e.target.value)} rows={2} className="bg-white" /></div>

          {/* ----- Advanced ERP fields (GST/TDS, donor, payment ref, etc.) ----- */}
          <button type="button" onClick={() => setShowAdvanced((v) => !v)} className="text-xs font-mono uppercase tracking-widest text-[var(--vs-secondary)] hover:underline self-start" data-testid="exp-advanced-toggle">
            {showAdvanced ? "− Hide" : "+ Show"} advanced fields (GST, TDS, donor, payment ref)
          </button>
          {showAdvanced && (
            <div className="vs-card p-3 space-y-3 bg-[var(--vs-bg-subtle)]" data-testid="exp-advanced-fields">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Paid to (legal name)</Label><Input data-testid="exp-paid-to-name" value={form.paid_to_name} onChange={(e) => set("paid_to_name", e.target.value)} placeholder="Defaults to payee" /></div>
                <div><Label>Paid to PAN</Label><Input data-testid="exp-paid-to-pan" value={form.paid_to_pan} onChange={(e) => set("paid_to_pan", e.target.value.toUpperCase())} placeholder="ABCDE1234F" maxLength={10} className="font-mono" /></div>
                <div><Label>Payment ref (UTR / cheque #)</Label><Input data-testid="exp-payment-ref" value={form.payment_reference} onChange={(e) => set("payment_reference", e.target.value)} className="font-mono" /></div>
                <div><Label>Project code</Label><Input data-testid="exp-project-code" value={form.project_code} onChange={(e) => set("project_code", e.target.value)} placeholder="e.g. CSR-2026-001" className="font-mono" /></div>
                <div><Label>GST number</Label><Input data-testid="exp-gst-no" value={form.gst_number} onChange={(e) => set("gst_number", e.target.value.toUpperCase())} maxLength={15} className="font-mono" /></div>
                <div><Label>GST amount (₹)</Label><Input data-testid="exp-gst-amt" type="number" step="0.01" value={form.gst_amount} onChange={(e) => set("gst_amount", e.target.value)} className="font-mono" /></div>
                <div><Label>TDS section</Label><Input data-testid="exp-tds-section" value={form.tds_section} onChange={(e) => set("tds_section", e.target.value)} placeholder="e.g. 194C" className="font-mono" /></div>
                <div><Label>TDS amount (₹)</Label><Input data-testid="exp-tds-amt" type="number" step="0.01" value={form.tds_amount} onChange={(e) => set("tds_amount", e.target.value)} className="font-mono" /></div>
                <div className="col-span-2"><Label>CSR / Donor project</Label><Input data-testid="exp-csr-project" value={form.csr_project} onChange={(e) => set("csr_project", e.target.value)} placeholder="e.g. ABC Foundation Q4 grant" /></div>
              </div>
            </div>
          )}

          <Button type="submit" disabled={busy} data-testid="exp-submit" className="bg-[var(--vs-secondary)] text-[var(--vs-bg)]">
            {busy ? "Saving…" : "Record expenditure"}
          </Button>

          {voucherFor && (
            <div className="mt-3 p-3 rounded-md border border-[var(--vs-warning)]/30 bg-[var(--vs-warning)]/10">
              <div className="text-xs">Recorded #{voucherFor.id.slice(0,8)} — attach voucher?</div>
              <label className="mt-2 flex items-center justify-center gap-2 px-3 py-2 rounded-md border-2 border-dashed border-[var(--vs-secondary)] cursor-pointer text-xs">
                <CloudArrowUp size={14} weight="duotone" /> Upload voucher
                <input data-testid="exp-voucher-input" type="file" accept="application/pdf,image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => uploadVoucher(voucherFor.id, e.target.files?.[0])} />
              </label>
            </div>
          )}
        </form>

        <div className="lg:col-span-2 vs-card overflow-x-auto self-start">
          <div className="px-4 py-3 flex items-center justify-between border-b border-[var(--vs-border)]">
            <div className="eyebrow">Recent expenditure</div>
            <div className="text-xs text-[var(--vs-text-secondary)]">Total: <span className="font-mono font-semibold text-[var(--vs-danger)]" data-testid="exp-list-total">{fmt(total)}</span> · {rows.length} entries</div>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-[var(--vs-bg-subtle)]">
              <tr className="text-left text-[11px] uppercase font-mono tracking-widest text-[var(--vs-text-secondary)]">
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3">Category</th>
                <th className="px-4 py-3">Payee</th>
                <th className="px-4 py-3 text-right">Amount</th>
                <th className="px-4 py-3">Mode</th>
                <th className="px-4 py-3">Voucher</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-[var(--vs-border)]" data-testid={`exp-row-${r.id}`}>
                  <td className="px-4 py-3 font-mono text-xs">{r.date}</td>
                  <td className="px-4 py-3 capitalize">{r.category}</td>
                  <td className="px-4 py-3">{r.payee}</td>
                  <td className="px-4 py-3 text-right font-mono">{fmt(r.amount)}</td>
                  <td className="px-4 py-3 text-[10px]"><Badge variant="outline">{r.mode?.toUpperCase()}</Badge></td>
                  <td className="px-4 py-3">{r.voucher_path ? <CheckCircle size={14} className="text-[var(--vs-success)]" /> : <span className="text-[var(--vs-text-secondary)] text-xs">—</span>}</td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-[var(--vs-text-secondary)]">No expenditure recorded yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </DashboardLayout>
  );
}
